import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { toggleSwitch } from '../../reducers/switch';
import EyeIcon from '../../assets/icons/eye-close.svg?react';

import {
  BulkUploadUserGetResponseDto,
  Jobs,
  JobsStatusEnum,
} from '@EHSNavigator/backend';
import { useNavigate } from 'react-router';
import Pagination from '../../components/CustomPagination';
import NoItemsFound from '../../components/NoItemsFound';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import ModalComponent from '../../components/ModalComponent';
import { toggleModal } from '../../reducers/modalToggle';
import ReviewTaskTableModal from '../../components/ReviewTaskTableModal';
import Loader from '../../components/Loader';

interface TaskReviewProps {
  otherClasses?: string;
  data: BulkUploadUserGetResponseDto;
  roles: { label: string; value: string }[];
  taskReviewLoading?: boolean;
  params: {
    limit: number;
    page: number;
  };
}

const TaskReview: React.FC<TaskReviewProps> = ({
  otherClasses,
  data,
  roles,
  params,
  taskReviewLoading,
}) => {
  const navigate = useNavigate();

  const noDataMessage = 'No task review found';

  const updateQuery = (queryName: string, queryValue: string) => {
    const params = new URLSearchParams(window.location.search);

    if (queryName === 'search') {
      params.set('page', '1');
    }

    if (queryName === 'page' && queryValue === '1') {
      params.delete('page');
    } else {
      params.set(queryName, queryValue);
    }

    navigate(`/task-review?${params.toString()}`);
  };

  const taskReviewClasses = clsx('flex flex-1 flex-col w-full' + otherClasses);
  const [state, setState] = useState({
    limit: params?.limit,
    page: params?.page,
  });
  const [selectedJob, setSelectedJob] = useState<Jobs | null>(null);

  const dispatch = useDispatch();
  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  const prevFiltersRef = useRef({});
  const isFirstRun = useRef(true);

  useEffect(() => {
    const filters = {
      limit: state.limit,
    };
    let shouldResetPage = false;
    if (!isFirstRun.current) {
      const filtersChanged =
        JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
      if (filtersChanged) {
        shouldResetPage = true;
        setState((prev) => ({ ...prev, page: 1 }));
      }
    }
    const paramsSearch = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'limit' && Number(value) === 10) {
        return;
      }
      if (value) paramsSearch.set(key, value.toString());
    });
    if (state.page !== 1) {
      paramsSearch.set('page', state.page.toString());
    }
    if (shouldResetPage) {
      paramsSearch.delete('page');
      setState((prev) => ({ ...prev, page: 1 }));
    }
    prevFiltersRef.current = filters;
    if (!isFirstRun.current) {
      navigate(`/task-review?${paramsSearch.toString()}`);
    }
    isFirstRun.current = false;
  }, [state.page, state.limit]);

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    updateQuery('page', String(newPage));
  };

  return (
    <div className={taskReviewClasses} data-testid="departments">
      <div className="flex items-start gap-2.5">
        <button
          className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5 lg:hidden"
          onClick={() => dispatch(toggleSwitch())}
        >
          <ToggleIcon />
        </button>
        <div className="flex w-full flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
          <div>
            <h1 className="h3">Task Review</h1>
            <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 hidden text-xl leading-6 font-medium lg:block">
              Displays recent bulk upload tasks (limited to the last two months)
            </p>
          </div>
        </div>
      </div>
      <p className="font-inter-variable text-neutral-tones-text/80 -tracking-text-area mt-15 text-lg leading-5 font-medium lg:hidden">
        Displays recent bulk upload tasks (limited to the last two months)
      </p>
      <div className="flex-1">
        {taskReviewLoading ? (
          <div>
            <Loader
              variant="compact"
              otherClasses="bg-neutral-tones-background/50 z-20 backdrop-blur-[2px] lg:!w-[calc(100%-310px)] lg:right-0 lg:left-auto !fixed !top-0 left-0 h-dvh"
            />
          </div>
        ) : data?.jobs?.length > 0 ? (
          <div className="global-scrollbar-style mt-12 min-h-52 overflow-auto px-0.5">
            <div className="w-full min-w-5xl">
              <div className="border-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b pb-6">
                <h6 className="text-neutral-tones-text/50 font-inter-variable -tracking-text-area text-form-label col-span-1 font-bold lg:text-lg lg:leading-5">
                  Sr.
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-3 font-bold lg:text-lg lg:leading-5">
                  Created By
                </h6>

                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                  Started At
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                  Completed At
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                  Progress
                </h6>
                <h6 className="text-neutral-tones-text font-inter-variable -tracking-text-area text-form-label col-span-2 font-bold lg:text-lg lg:leading-5">
                  Status
                </h6>
              </div>
              <div>
                {data?.jobs.map((job: Jobs, index: number) => (
                  <div
                    key={index}
                    className="border-b-neutral-tones-text/50 grid grid-cols-12 gap-4 border-b py-5"
                  >
                    <p className="text-neutral-tones-text/50 font-inter-variable lg:-tracking-form-input col-span-1 flex items-center text-sm leading-4 font-bold lg:text-base lg:leading-5">
                      {(state.page - 1) * state.limit + index + 1}
                    </p>
                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-3 flex items-center text-sm leading-4 font-normal capitalize lg:text-base lg:leading-5">
                      {job.user?.fullName || job.createdByPlatformOwner}
                    </p>
                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                      {moment(job?.startDate).format('ddd D MMM h:mm A')}
                    </p>
                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                      {job?.completedAt !== null
                        ? moment(job?.completedAt).format('ddd D MMM h:mm A')
                        : '-'}
                    </p>
                    <p className="text-neutral-tones-text/80 font-inter-variable lg:-tracking-form-input col-span-2 flex items-center text-sm leading-4 font-normal lg:text-base lg:leading-5">
                      {`${job?.progress}%`}
                    </p>

                    <div className="col-span-2 flex items-center justify-between">
                      <p
                        className={clsx(
                          'font-inter-variable lg:-tracking-form-input flex items-center text-sm leading-4 font-semibold capitalize lg:text-base lg:leading-5',
                          job?.status === JobsStatusEnum.Completed
                            ? 'text-brand-papery'
                            : job?.status === JobsStatusEnum.Waiting
                              ? 'text-brand-fair-orange'
                              : job?.status === JobsStatusEnum.Failed
                                ? 'text-brand-posh-pink'
                                : 'text-brand-byzantine',
                        )}
                      >
                        {job?.status}
                      </p>
                      <button
                        disabled={
                          job?.status === JobsStatusEnum.Active ||
                          job?.status === JobsStatusEnum.Pending ||
                          job?.status === JobsStatusEnum.Waiting
                        }
                        onClick={() => {
                          setSelectedJob(job);
                          dispatch(toggleModal('review-task'));
                        }}
                        className="border-neutral-tones-text/10 bg-card-backgrounds/5 text-brand-byzantine disabled:text-neutral-tones-text/50 flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border disabled:cursor-not-allowed"
                      >
                        <EyeIcon className="h-5 w-5 [&>path]:fill-current" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <NoItemsFound message={noDataMessage} />
        )}
      </div>
      {data?.jobs?.length > 0 && (
        <Pagination
          currentPage={state.page}
          totalPages={data?.meta?.totalPages}
          totalItems={data?.meta?.total}
          limit={data?.meta?.limit}
          showingItems={data?.jobs?.length}
          onPageChange={handlePageChange}
          onLimitChange={(e) =>
            setState((prev) => ({ ...prev, limit: e, page: 1 }))
          }
        />
      )}

      {['review-task'].includes(modalName) && (
        <ModalComponent
          otherClasses="px-4 sm:px-0 py-4"
          additionalClass={clsx(
            'rounded-2xl p-5 lg:p-6 h-fit max-h-full max-w-3xl w-full',
          )}
          customClose={() => {
            dispatch(toggleModal(''));
            setSelectedJob(null);
          }}
        >
          <ReviewTaskTableModal
            selectedJob={selectedJob}
            roles={roles}
            handleClose={() => {
              dispatch(toggleModal(''));
              setSelectedJob(null);
            }}
          />
        </ModalComponent>
      )}
    </div>
  );
};

export default TaskReview;
